import { space } from '@/constants/tokens';
import { useHeaderHeight } from 'expo-router/react-navigation';
import { Platform, RefreshControl, useWindowDimensions } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { getTokenValue } from 'tamagui';

/**
 * The page frame's numbers, shared by the two containers that draw it —
 * [[PageScrollView]] (the default) and [[PageFlatList]] (a page whose body is a
 * long list). PageScrollView's doc block is the why behind every value here; this
 * module exists for one reason: so the two containers can't drift apart.
 */

/** The page gutter. Exported because a 2-column grid has to halve around it. */
export const PAGE_GUTTER = space.lg;

/**
 * How wide the app is ever allowed to draw. This is a phone layout — a
 * full-bleed 1400px hero and a two-column grid of 600px cards is not a design
 * that exists — so on a desktop browser the app stays a phone-width column and
 * the window's leftover width becomes margin. 430 is the design target (the
 * iPhone 14 Pro Max viewport the screens are drawn against).
 *
 * Applied by the app column in [[_layout]], web only: on native the window IS
 * the device, and a wide Android phone would otherwise get letterboxed.
 */
export const APP_MAX_WIDTH = 430;

/**
 * The width to measure against — the app COLUMN's, not the window's.
 *
 * The same thing on a phone. On a wide browser the two differ by a thousand
 * pixels, and anything deriving a size from the window (a grid column, a
 * markdown content width) would size itself to the window and overflow the
 * column it actually renders in. Reach for this over `useWindowDimensions`
 * anywhere the number feeds a LAYOUT.
 */
export function useFrameWidth() {
  const { width } = useWindowDimensions();
  return Platform.OS === 'web' ? Math.min(width, APP_MAX_WIDTH) : width;
}

/**
 * Header `screenOptions` that inset the left/right slots to the page gutter, so
 * a header control lines up with the content under it.
 *
 * Web-only, and zero elsewhere. On native the OS header applies its own standard
 * margin — and react-native-screens ignores these two options anyway, since they
 * belong to the JS header. Web is where expo-router falls back to that JS header
 * (see NativeStackView), and its only horizontal inset there is the safe area,
 * which a browser reports as 0 — so every control sits flush against the window.
 *
 * Spread into a Stack's `screenOptions`; nested stacks need their own copy,
 * since screenOptions don't inherit across navigators.
 */
const HEADER_SLOT_INSET = Platform.OS === 'web' ? PAGE_GUTTER : 0;

export const headerSlotInsetOptions = {
  headerLeftContainerStyle: { paddingStart: HEADER_SLOT_INSET },
  headerRightContainerStyle: { paddingEnd: HEADER_SLOT_INSET },
};

export function usePageFrame({
  headerTransparent = true,
  onRefresh,
  isRefreshing = false,
}: {
  headerTransparent?: boolean;
  onRefresh?: () => void;
  isRefreshing?: boolean;
}) {
  // The real header height, reported by the navigator that drew it (the native
  // header measures itself, so this tracks notch size, large titles and the
  // iOS-26 glass bar without a hardcoded number). It ALREADY includes the
  // safe-area top inset — don't add insets.top on top of it. Imported from
  // `expo-router/react-navigation` because expo-router vendors react-navigation:
  // a separately installed `@react-navigation/elements` would carry its own
  // HeaderHeightContext and the hook would throw.
  const headerHeight = useHeaderHeight();
  const { bottom } = useSafeAreaInsets();

  return {
    paddingTop: headerTransparent ? headerHeight + space.xl : space.xl,
    paddingBottom: space.xxl + bottom,
    refreshControl:
      onRefresh && Platform.OS !== 'web' ? (
        <RefreshControl
          refreshing={isRefreshing}
          onRefresh={onRefresh}
          // The same number as the top padding, deliberately: the spinner turns
          // where the content starts, not behind the header it scrolls under.
          progressViewOffset={headerTransparent ? space.xxxl : 0}
          // ios specific settings
          title=""
          tintColor={getTokenValue('$color.textDark')}
          // android specific settings
          progressBackgroundColor={getTokenValue('$color.background')}
          colors={[getTokenValue('$color.textDark')]}
          size="large"
        />
      ) : undefined,
  };
}
